import {
    CreationNavButton,
    LoadingSkeleton,
    OPEN_ORDER_STATUSES,
    OrderStatus,
    PageGridContainer,
    PageGridItem,
    Tab,
    TabBar,
    tokens,
} from 'ComponentLibrary'
import OrderListPageContent, { OrderSortOption, PortfolioMetadata } from './OrderListPageContent'
import { Box, Divider, Stack } from '@mui/material'
import { useState } from 'react'
import { OrderListFilter } from './OrderListFilter/OrderListFilter'
import { useGetCustomerUsers } from 'ClientPortal/src/api/Queries/users'
import { hasSomeFilter } from 'ClientPortal/src/pages/Orders/OrderList/OrderListPageContent.data'
import { Dayjs } from 'dayjs'
import { useGetAllPortfoliosValueOverview } from 'ClientPortal/src/api/Queries/value-overview'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useUrlState } from 'ComponentLibrary/src/hooks/useUrlState'
import { OrderListFilterStateSchema } from 'ClientPortal/src/pages/schemas.zod'
import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { LOCAL_STORAGE_KEYS, readFromLocalStorage, writeToLocalStorage } from 'ClientPortal/src/services/localStorage'
import { useTranslation } from 'react-i18next'

export function OrderListPage() {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderListPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const [statusCategory, setStatusCategory] = useState<'open' | 'closed' | 'cancelled'>('open')

    const [sortOption, setSortOption] = useState<OrderSortOption>(
        () => readFromLocalStorage<OrderSortOption>(LOCAL_STORAGE_KEYS.ORDER_LIST_SORT_OPTION) || 'orderDate'
    )

    const { urlState, setUrlState, resetUrlState } = useUrlState(OrderListFilterStateSchema, 'client-order-filter')

    const setPortfolio = (portfolio: PortfolioMetadata | undefined) => setUrlState((prev) => ({ ...prev, portfolio }))
    const setStatuses = (statuses: OrderStatus[]) => setUrlState((prev) => ({ ...prev, statuses }))
    const setAssignedCustomers = (assignedCustomers: number[]) => setUrlState((prev) => ({ ...prev, assignedCustomers }))
    const setTitleFilter = (titleFilter: string) => setUrlState((prev) => ({ ...prev, titleFilter }))
    const setStartValuationDate = (start: Dayjs | null) => {
        setUrlState((prev) => ({
            ...prev,
            valuationDateRange: { ...prev.valuationDateRange, start },
        }))
    }
    const setEndValuationDate = (end: Dayjs | null) =>
        setUrlState((prev) => ({ ...prev, valuationDateRange: { ...prev.valuationDateRange, end } }))
    const { statuses, assignedCustomers, titleFilter, valuationDateRange, portfolio } = urlState

    const { customerUsers, isCustomerUsersPending } = useGetCustomerUsers()
    const { multiPortfolioValueOverview, isMultiPortfolioValueOverviewPending } = useGetAllPortfoliosValueOverview()
    const isFiltersPending = isCustomerUsersPending || isMultiPortfolioValueOverviewPending

    const portfolios =
        multiPortfolioValueOverview?.portfolios
            .filter((portfolioOverview) => portfolioOverview.type !== 'synthetic')
            .map((portfolioOverview) => {
                return {
                    uuid: portfolioOverview.uuid,
                    title: portfolioOverview.title,
                }
            }) || []

    const statusCategoryConfig: Record<'open' | 'closed' | 'cancelled', OrderStatus[]> = {
        open: [...OPEN_ORDER_STATUSES],
        closed: ['done' as const],
        cancelled: ['canceled' as const],
    }

    const handleSortChange = (option: OrderSortOption) => {
        setSortOption(option)
        writeToLocalStorage(LOCAL_STORAGE_KEYS.ORDER_LIST_SORT_OPTION, option)
    }

    return (
        <PageGridContainer withBottomSpace withTopSpace withFooter>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Stack direction="row" gap={tokens.SizingSpacing150} justifyContent="space-between">
                    <Headline variant="headline1" title={tTerminology('orders')} fullWidth={false} />
                    <CreationNavButton label={t('newOrder')} href={STATIC_APP_PATHS.createOrderPage} color="royal" />
                </Stack>
            </PageGridItem>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <OrderListFilter
                    defaultOrderTitleFilter={titleFilter}
                    isPending={isFiltersPending}
                    customerUsers={customerUsers}
                    setStatuses={setStatuses}
                    statuses={statuses}
                    setAssignedCustomers={setAssignedCustomers}
                    assignedCustomers={assignedCustomers}
                    setTitleFilter={setTitleFilter}
                    setStartValuationDate={setStartValuationDate}
                    setEndValuationDate={setEndValuationDate}
                    valuationDateRange={valuationDateRange}
                    portfolioOptions={portfolios}
                    portfolioFilter={portfolio}
                    setPortfolioFilter={setPortfolio}
                    resetAllFilters={resetUrlState}
                    areFiltersEmpty={!hasSomeFilter(urlState)}
                    sortOption={sortOption}
                    onSortChange={handleSortChange}
                />
            </PageGridItem>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                {isFiltersPending ? (
                    <StyledTabsLoading />
                ) : (
                    <Box
                        sx={{
                            marginBottom: tokens.SizingSpacing150,
                            opacity: hasSomeFilter(urlState) ? 0 : 1,
                            visibility: hasSomeFilter(urlState) ? 'hidden' : 'visible',
                            marginTop: hasSomeFilter(urlState) ? `-${tokens.SizingSpacing600}` : 0,
                            transition: 'opacity 0.4s linear, visibility 0.4s linear, margin-top 0.4s ease-in',
                        }}
                    >
                        <TabBar selectedTab={statusCategory} handleChange={(newValue) => setStatusCategory(newValue)}>
                            <Tab value="open" label={t('tabs.open')} />
                            <Tab value="closed" label={t('tabs.closed')} />
                            <Tab value="cancelled" label={t('tabs.cancelled')} />
                        </TabBar>
                    </Box>
                )}
            </PageGridItem>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <OrderListPageContent
                    customerUsers={customerUsers}
                    filterState={urlState}
                    key={`${statusCategory}-orders`}
                    category={statusCategory}
                    setCategory={setStatusCategory}
                    defaultStatuses={statusCategoryConfig[statusCategory]}
                    resetFilters={resetUrlState}
                    sortOption={sortOption}
                />
            </PageGridItem>
        </PageGridContainer>
    )
}

const StyledTabsLoading = () => {
    return (
        <Stack direction="column" gap={tokens.SizingSpacing100} marginBottom={tokens.SizingSpacing150}>
            <Stack direction="row" gap={tokens.SizingSpacing300}>
                <LoadingSkeleton width="80px" height="29px" sx={{ transform: 'none', borderRadius: '15px' }} />
                <LoadingSkeleton width="160px" height="29px" sx={{ transform: 'none', borderRadius: '15px' }} />
                <LoadingSkeleton width="130px" height="29px" sx={{ transform: 'none', borderRadius: '15px' }} />
            </Stack>
            <Divider sx={{ borderBottom: `${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}` }} />
        </Stack>
    )
}
